import { useRef } from 'react'
import { Star } from 'lucide-react'
import { useTranslation } from '@/i18n'

/**
 * Accessible 1–5 star control (radiogroup semantics + arrow keys).
 */
const StarRating = ({
  value,
  onChange,
  label,
}: {
  value: number
  onChange: (value: number) => void
  label: string
}) => {
  const { t } = useTranslation('help_agent')
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([])

  // Arrow keys must move BOTH selection and DOM focus, else the user stays
  // focused on the previously-selected star (greptile #18062).
  const selectAndFocus = (next: number) => {
    onChange(next)
    buttonsRef.current[next - 1]?.focus()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault()
      selectAndFocus(Math.min(5, value + 1))
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault()
      selectAndFocus(Math.max(1, value - 1))
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="flex items-center justify-center gap-2"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= value
        return (
          <button
            key={star}
            ref={(el) => {
              buttonsRef.current[star - 1] = el
            }}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={t('feedback.stars_label', { count: star })}
            tabIndex={value === star || (value === 0 && star === 1) ? 0 : -1}
            onClick={() => onChange(star)}
            className="rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Star
              className={
                active
                  ? 'size-6 fill-amber-400 text-amber-400'
                  : 'size-6 text-gray-300 dark:text-dark-border'
              }
            />
          </button>
        )
      })}
    </div>
  )
}

export default StarRating
